/**
 * Whether a model's credentials are complete, at a glance.
 *
 * Registering a model has always succeeded whether or not a credential existed
 * anywhere, so a row that could never run looked exactly like a working one
 * until someone ran a prompt against it and got a 503. The backend now computes
 * this on every read; this is where it becomes visible.
 *
 * Deliberately not a claim that the model WORKS — only that nothing required is
 * blank. A valid-looking key that the provider rejects still reads as
 * configured, which is what the Verify action is for.
 */
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import type { AiModelConfigurationStatus } from '../../types';

interface Props {
  status?: AiModelConfigurationStatus | null;
  /** Renders the reason inline instead of only in a tooltip. */
  verbose?: boolean;
  id?: string;
}

const ConfigurationStatusTag = ({ status, verbose, id = 'cfg' }: Props) => {
  if (!status) return <span className="text-gray-400">—</span>;

  const reason = status.missing?.length
    ? `Missing: ${status.missing.join(', ')}`
    : status.detail || '';

  const presentation = {
    configured: { severity: 'success' as const, label: 'Configured', icon: 'pi pi-check' },
    missing_credentials: { severity: 'warning' as const, label: 'Not configured', icon: 'pi pi-exclamation-triangle' },
    unresolvable: { severity: 'danger' as const, label: 'Broken', icon: 'pi pi-times' },
  }[status.status] ?? { severity: 'info' as const, label: status.status, icon: 'pi pi-question' };

  const anchor = `cfg-tag-${id}`;

  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className={anchor} data-pr-tooltip={reason || undefined} data-pr-position="top">
        {reason && <Tooltip target={`.${anchor}`} />}
        <Tag severity={presentation.severity} icon={presentation.icon} value={presentation.label} />
      </span>
      {verbose && reason && (
        <span className="text-xs text-ink-muted">{reason}</span>
      )}
    </span>
  );
};

export default ConfigurationStatusTag;
